//
// New API Response details
// https://pragmaticplay.atlassian.net/wiki/spaces/LE/pages/3850469413/Restructured+API+Endpoints
import type { SelectByType, WithExtraFields, HowToPlaySection, PromotionType, BonusCardPromoType } from './utils';

export enum PromoObjectType {
  Promotions = 'PROMOTIONS',
  Error = 'ERROR',
}

type RoundsType = 'DAILY' | 'TOTAL' | null;

export interface PromotionsApiResponse<T extends PromoObjectType = PromoObjectType> {
  objectType: T;
  data: SelectByType<
    T,
    {
      [PromoObjectType.Promotions]: Array<PromotionJSON>;
      [PromoObjectType.Error]: string;
    }
  >;
  AdditionalData: SelectByType<
    T,
    {
      [PromoObjectType.Promotions]: {
        TOURNAMENT_WIN_NOTIFICATION?: Array<PromotionWinNotificationJSON>;
        CASHBACK_WIN_NOTIFICATION?: Array<PromotionWinNotificationJSON>;
      };
      [PromoObjectType.Error]: never;
    }
  >;
}

export enum ConsentRequestStatus {
  OK = 'OK',
  FAILURE = 'FAILURE',
}

export enum ConsentStatusCode {
  SUCCESS = 'OPT000',
  NO_OPT_OUT_JURIDICTION = 'OPT001',
  NOT_APPLICABLE = 'OPT002',
  OPT_IN_LIMIT_EXCEEDED = 'OPT003',
  ALREADY_OPTED = 'OPT004',
  CANNOT_PROCESS = 'OPT005',
  DB_ERROR = 'OPT006',
  INTERNAL_SERVER_ERROR = 'OPT007',
}

export interface ConsentResponse {
  status: ConsentRequestStatus;
  errorCode: ConsentStatusCode;
  description: string;
  promoId: number;
  promoType: PromotionType;
}

export interface ConsentAPIResponse {
  optInResponses: Array<ConsentResponse>;
  error?: string;
}
export interface InstantPointsApiResponse {
  isNewPromoAvailable: boolean;
  data: Record<string, PromotionPointsJSON>;
}

export interface LeaderboardAPIResponse {
  description: string;
  error: string;
  isRoundLimitExceeded: boolean;
  tournamentId: number;
  screenNameHidden: boolean;
  data: Array<LeaderboardPlayerDetails>;
}

export interface LeaderboardAroundMeAPIResponse {
  type: 'F' | 'AM'; // F -> Full, AM -> Around me
  platformTournamentPlayerId: number;
  platformTournamentId: number;
  leaderboardItems: Array<LeaderboardPlayerDetails> | null;
}

export interface PrizesAndWinnersAPIResponse {
  [key: string]: {
    prizes: Array<
      {
        prizeId: number;
        currentSize: number;
        originalSize: number;
      } & PromotionPrizeJSON
    >;
    winners: Array<{
      amount: number | null;
      casinoUserId: string;
      countryId: string;
      currency: string;
      envId: number;
      gift: string | null;
      ppPlayerId: number;
      prizeId: number;
      screenName: string;
      winDate: number;
    }>;
    screenNameHidden: boolean;
  };
}

export interface CardsAndWinnersAPIResponse {
  [key: string]: {
    cards: Array<{
      amount?: number;
      betMultiplier?: number;
      totalCount: number;
      remainingCount: number;
      type: string;
    }>;
    winners: Array<{
      ppPlayerId: number;
      userId: string;
      screenName: string;
      amount: number;
    }>;
    screenNameHidden: boolean;
  };
}

export interface CashbackScoreAPIResponse {
  [key: string]: {
    cashbackScore: number;
    totalScore: number;
    cashbackPercent: number;
    isRoundLimitExceeded: boolean;
    cashbackId: number;
  };
}

export type PromotionJSON =
  | WithExtraFields<TournamentPromotionJSON>
  | WithExtraFields<CashbackPromotionJSON>
  | WithExtraFields<PrizeDropPromotionJSON>
  | WithExtraFields<BonusCardPromotionJSON>;

export interface PromotionWinNotificationJSON {
  name: string;
  promoId: number;
  promoType: PromotionType.Tournament | PromotionType.Cashback;
  envId: number;
  winType: 'G' | 'C' | 'BM';
  autoPay: boolean;
  extraData: {
    type?: 'CASHBACK' | 'CASHBOOST';
    PRIZE_DETAILS?: PrizeDetails;
    minCashbackAmount?: number;
    maxCashbackAmount?: number;
  };
  prize: string;
  currency: string;
  rule: string;
  endDate: string;
  dynamicFields?: Record<string, DynamicField>;
  minBetLimit: number;
}

export type BonusCardPromotionPointsJSON = {
  promoType: PromotionType.BonusCard;
  isBonusCardCancelled: boolean;
  isCardsLimitExceeded: boolean;
};

export type PrizeDropPromotionPointsJSON = {
  promoType: PromotionType.Prizedrop;
  isRoundLimitExceeded: boolean;
};

export type CashBackPromotionPointsJSON = {
  promoType: PromotionType.Cashback;
  isRoundLimitExceeded: boolean;
};

export type TournamentPromotionPointsJSON = {
  promoType: PromotionType.Tournament;
  totalScore: number;
  winningStreak: number;
  lastWonScore: number;
  lastGameId: string;
  showBadge: number;
  isRoundLimitExceeded: boolean;
};

export type PromotionPointsJSON =
  | TournamentPromotionPointsJSON
  | CashBackPromotionPointsJSON
  | PrizeDropPromotionPointsJSON
  | BonusCardPromotionPointsJSON;

//
// Individual JSON Promotion definitions
//
export interface TournamentPromotionJSON extends PromotionSharedJSON {
  promoType: PromotionType.Tournament;
  tournamentId: number;
  platformTournamentId: number;
  TOURNAMENT: PromotionTypeSharedJSON;
  TOURNAMENT_DETAILS: PromotionTypeDetailsSharedJSON & {
    /*
     GP -> GenericPointsWinCriterionPayload, 
     TB -> Total bet win criterion, 
     TP -> Total point win criterion (sum of win multiplier)
     WS -> Win Streak
    */
    winCriterion: 'GP' | 'TB' | 'TP' | 'WS';
    /* what kind of round is this?	
      DAILY Limit is applied on daily basis.
			TOTAL Limit is applied throughout the tournament.
    */
    roundsType: RoundsType;
    prizes: Array<{ from: number; to: number } & PromotionPrizeJSON>;
  };
  LEADERBOARD_AROUNDME: LeaderboardAroundMeAPIResponse;
  LEADERBOARD: LeaderboardAPIResponse;
  PRIZE_DETAILS: PrizeDetails;
}

export interface CashbackPromotionJSON extends PromotionSharedJSON {
  promoType: PromotionType.Cashback;
  cashbackId: number;
  platformCashbackId: number;
  CASHBACK: PromotionTypeSharedJSON;
  CASHBACK_DETAILS: PromotionTypeDetailsSharedJSON & {
    id: number;
    roundsType: RoundsType;
    cashbackValues: {
      type: 'CASHBACK' | 'CASHBOOST';
      minCashbackAmount: number;
      maxCashbackAmount: number;
      displayScore: boolean;
    };
  };
  score: {
    cashbackId: number;
    cashbackScore: number;
    totalScore: number;
    cashbackPercent: number;
    isRoundLimitExceeded: boolean;
  };
}

export interface BonusCardPromotionJSON extends PromotionSharedJSON {
  promoType: PromotionType.BonusCard;
  bonuscardId: number;
  minBet: number;
  maxWinLimit: number;
  BONUSCARD: {
    cards: Array<BonusCardJSON>;
  };
  CARD_DETAILS: CardDetails;
}

export interface PrizeDropPromotionJSON extends PromotionSharedJSON {
  promoType: PromotionType.Prizedrop;
  prizedropId: number;
  platformPrizedropId: number;
  maxBetLimitForBetMultiplier?: number;
  marketingShownTime: number;
  PRIZEDROP: PromotionTypeSharedJSON & {
    showWinnersList: boolean;
    currency: string;
    roundsType: RoundsType;
    prizes: Array<
      {
        originalSize: number;
        prizeId: number;
        currentSize: number;
      } & PromotionPrizeJSON
    >;
  };
  PRIZEDROP_DETAILS: PromotionTypeDetailsSharedJSON;
  PRIZE_DETAILS: PrizeDetails;
}

//
// Shared field definitions between all promotion types
//
export interface PromotionSharedJSON {
  name: string;
  startDate: number;
  endDate: number;
  gameIds: string;
  opt: 'opted' | 'non-opted' | 'pop' | 'NA';
  consent: boolean;
  feedbackOn?: number | null;
  campaignId: number | null;
  campaignEndDate: number | null;
  dynamicFields?: Record<string, DynamicField>;
}

export interface PromotionTypeSharedJSON {
  minBetLimit?: number;
  maxBetLimitsForBetMultiplier?: Array<{ cur: string; bet: number }> | null;
  campaignEndDate: number | null;
  campaignId: number | null;
  envId: number;
}

export type DynamicField = {
  value: string;
  displayCurrency: boolean;
};

export interface PromotionTypeDetailsSharedJSON {
  rule: string;
  shortRule: string;
  dynamicFields?: Record<string, DynamicField>;
  howToPlayFirstRule: HowToPlaySection | null;
  howToPlaySecondRule: HowToPlaySection | null;
  howToPlayThirdRule: HowToPlaySection | null;
}

//
// Individual promotion field definitions
//
export interface PromotionPrizeJSON {
  type: 'G' | 'C' | 'BM';
  amount?: number | null;
  gift?: string | null;
  betMultiplier?: number | null;
}

export interface BonusCardJSON {
  type: BonusCardPromoType;
  amount?: number | null;
  betMultiplier?: number | null;
  totalCount: number;
  remainingCount: number;
}

export interface PrizeDetails {
  prizeCount: number; // Total no of prizes
  prizeAmount: number; // Total prize amount
  highestPrize: string;
  tournamentPrizes?: Array<{ from: number; to: number } & PromotionPrizeJSON>;
}

export interface CardDetails {
  cardsCount: number;
  prizeAmount: number;
  highestPrize: number;
}

export interface LeaderboardPlayerDetails {
  platformTournamentPlayerId: number;
  position: number;
  score: number;
  scoreDate: number;
  screenName: string | null;
}
